import { useState } from 'react';
import { Copy, Check, ExternalLink, X, AlertTriangle, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import { formatPaiseToRupees } from '../schemas';
import { FindingKindBadge } from './FindingKindBadge';
import { FindingStatusBadge } from './FindingStatusBadge';
import type { ReconciliationFindingRow } from '../types';

interface SideBySideDiffViewerProps {
  finding: ReconciliationFindingRow | null;
  onClose: () => void;
  onResolve: (finding: ReconciliationFindingRow) => void;
}

function parseJsonSafely(jsonStr: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(jsonStr);
    return typeof parsed === 'object' && parsed !== null ? parsed : { raw: jsonStr };
  } catch {
    return { raw: jsonStr };
  }
}

export function SideBySideDiffViewer({
  finding,
  onClose,
  onResolve,
}: SideBySideDiffViewerProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showRawInternal, setShowRawInternal] = useState(false);
  const [showRawGateway, setShowRawGateway] = useState(false);

  if (!finding) return null;

  const internalObj = parseJsonSafely(finding.internalState);
  const gatewayObj = parseJsonSafely(finding.gatewayState);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard?.writeText(text)?.catch(() => undefined);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Detect mismatches
  const internalStatus = String(internalObj.status ?? internalObj.paymentStatus ?? '');
  const gatewayStatus = String(gatewayObj.status ?? '');
  const statusMismatch =
    internalStatus && gatewayStatus && internalStatus.toLowerCase() !== gatewayStatus.toLowerCase();

  const internalAmount =
    typeof internalObj.amountPaise === 'number'
      ? internalObj.amountPaise
      : typeof internalObj.amount === 'number'
        ? internalObj.amount
        : null;
  const gatewayAmount =
    typeof gatewayObj.amount === 'number'
      ? gatewayObj.amount
      : typeof gatewayObj.amountPaise === 'number'
        ? gatewayObj.amountPaise
        : null;
  const amountMismatch =
    internalAmount !== null && gatewayAmount !== null && internalAmount !== gatewayAmount;

  const providerOrderId = String(
    gatewayObj.id ?? gatewayObj.orderId ?? internalObj.providerOrderId ?? '',
  );

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, x: '100%' }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          data-testid="finding-diff-drawer"
          className="w-full max-w-3xl h-full bg-surface-container-lowest border-l border-border-subtle flex flex-col shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-border-subtle flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FindingKindBadge kind={finding.kind} />
                <FindingStatusBadge row={finding} />
              </div>
              <h2 className="text-lg font-bold text-on-surface">Finding Discrepancy Inspection</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs font-mono text-on-surface-variant truncate max-w-md">
                  ID: {finding.id}
                </span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(finding.id, 'id')}
                  className="p-1 hover:text-primary transition-colors text-on-surface-variant"
                  title="Copy finding ID"
                >
                  {copiedKey === 'id' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl text-on-surface-variant hover:text-on-surface hover:bg-white/5 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Meta & Correlation banner */}
          <div className="px-6 py-3 bg-surface-container/40 border-b border-border-subtle flex flex-wrap items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              <span>
                <span className="text-on-surface-variant">Entity: </span>
                <span className="font-mono font-bold text-on-surface">{finding.entityType} ({finding.entityId})</span>
              </span>
              <span>
                <span className="text-on-surface-variant">Detected: </span>
                <span className="font-mono text-on-surface">{new Date(finding.detectedAt).toLocaleString('en-IN')}</span>
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-on-surface-variant">Correlation:</span>
              <span className="font-mono text-on-surface font-semibold truncate max-w-xs">{finding.correlationId}</span>
              <button
                type="button"
                onClick={() => copyToClipboard(finding.correlationId, 'corr')}
                className="hover:text-primary transition-colors"
                title="Copy correlation ID"
              >
                {copiedKey === 'corr' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* Side-by-side State Diff View */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Warning if mismatch detected */}
            {(statusMismatch || amountMismatch) && (
              <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-start gap-3 text-rose-300 text-xs">
                <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">Automated Discrepancy Flag</p>
                  <p className="mt-0.5 text-rose-300/90">
                    {statusMismatch && `Status conflict detected: Internal '${internalStatus}' vs Gateway '${gatewayStatus}'. `}
                    {amountMismatch && `Amount mismatch: Internal ${formatPaiseToRupees(internalAmount)} vs Gateway ${formatPaiseToRupees(gatewayAmount)}.`}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left Panel: Internal State */}
              <div className="p-4 rounded-2xl border border-border-subtle bg-surface-container/30 flex flex-col">
                <div className="flex items-center justify-between pb-3 border-b border-border-subtle mb-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-primary">
                    InviteCloud Internal State
                  </h4>
                  <button
                    type="button"
                    onClick={() => setShowRawInternal(!showRawInternal)}
                    className="text-[11px] text-on-surface-variant hover:text-on-surface transition-colors"
                  >
                    {showRawInternal ? 'View Formatted' : 'View Raw JSON'}
                  </button>
                </div>

                {showRawInternal ? (
                  <pre className="text-xs font-mono p-3 bg-black/40 rounded-xl overflow-x-auto text-emerald-300 whitespace-pre-wrap">
                    {JSON.stringify(internalObj, null, 2)}
                  </pre>
                ) : (
                  <div className="space-y-2 text-xs">
                    {Object.entries(internalObj).map(([k, v]) => (
                      <div key={k} className="flex justify-between items-center py-1 border-b border-border-subtle/40">
                        <span className="text-on-surface-variant font-medium capitalize">{k.replace(/([A-Z])/g, ' $1')}:</span>
                        <span className={cn(
                          'font-mono font-semibold truncate max-w-[160px]',
                          k === 'status' && statusMismatch && 'text-rose-400 bg-rose-500/10 px-1 rounded',
                          (k === 'amountPaise' || k === 'amount') && 'text-on-surface',
                        )}>
                          {k.includes('amount') || k.includes('Paise') ? formatPaiseToRupees(Number(v)) : String(v)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right Panel: Gateway State */}
              <div className="p-4 rounded-2xl border border-border-subtle bg-surface-container/30 flex flex-col">
                <div className="flex items-center justify-between pb-3 border-b border-border-subtle mb-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-cyan-400">
                    Razorpay Gateway State
                  </h4>
                  <button
                    type="button"
                    onClick={() => setShowRawGateway(!showRawGateway)}
                    className="text-[11px] text-on-surface-variant hover:text-on-surface transition-colors"
                  >
                    {showRawGateway ? 'View Formatted' : 'View Raw JSON'}
                  </button>
                </div>

                {showRawGateway ? (
                  <pre className="text-xs font-mono p-3 bg-black/40 rounded-xl overflow-x-auto text-cyan-300 whitespace-pre-wrap">
                    {JSON.stringify(gatewayObj, null, 2)}
                  </pre>
                ) : (
                  <div className="space-y-2 text-xs">
                    {Object.entries(gatewayObj).map(([k, v]) => (
                      <div key={k} className="flex justify-between items-center py-1 border-b border-border-subtle/40">
                        <span className="text-on-surface-variant font-medium capitalize">{k.replace(/([A-Z])/g, ' $1')}:</span>
                        <span className={cn(
                          'font-mono font-semibold truncate max-w-[160px]',
                          k === 'status' && statusMismatch && 'text-rose-400 bg-rose-500/10 px-1 rounded',
                          (k === 'amount' || k === 'amountPaise') && 'text-on-surface',
                        )}>
                          {k.includes('amount') || k.includes('Paise') ? formatPaiseToRupees(Number(v)) : String(v)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* External Links & Audit context */}
            <div className="p-4 rounded-xl border border-border-subtle bg-surface-container/20 space-y-2">
              <h5 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                External Cross-References
              </h5>
              <div className="flex flex-wrap gap-3 pt-1">
                {providerOrderId && providerOrderId.startsWith('order_') && (
                  <a
                    href={`https://dashboard.razorpay.com/app/orders/${providerOrderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border-subtle bg-surface-container/60 hover:bg-surface-container text-xs text-on-surface hover:text-primary transition-all font-mono"
                  >
                    <span>Razorpay Order: {providerOrderId}</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}

                {finding.entityType === 'Invoice' && (
                  <span className="text-xs text-on-surface-variant py-1.5">
                    Target Invoice: <span className="font-mono text-on-surface">{finding.entityId}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Prior Resolution info if already resolved */}
            {finding.resolvedAt && (
              <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-300 space-y-1">
                <p className="font-bold flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-emerald-400" />
                  Resolution Recorded
                </p>
                <p className="text-emerald-300/90 font-mono">
                  Resolved by {finding.resolvedBy ?? 'System'} on {new Date(finding.resolvedAt).toLocaleString('en-IN')}
                </p>
                {finding.resolution && (
                  <p className="pt-1 text-on-surface italic">&ldquo;{finding.resolution}&rdquo;</p>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t border-border-subtle bg-surface-container/60 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-border-subtle text-sm text-on-surface-variant hover:text-on-surface hover:bg-white/5 transition-all"
            >
              Close
            </button>

            {!finding.resolvedAt && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onResolve(finding);
                }}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-primary hover:bg-primary-hover text-on-primary font-bold text-sm shadow-md transition-all"
              >
                <span>Resolve Finding</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
